import { useId, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { formatCents, formatQuantityMilli, type ProductOption } from '@kontor/shared';
import { api, queryString } from '../../api';
import { useSession } from '../../session';
import '../../components/person-picker/person-picker.css';

/**
 * Produkt für eine Angebotsposition suchen (KON-113): Bezeichnung, Artikelnummer oder EAN. Ohne Eingabe stehen die ersten Produkte nach Name da, damit man auch
 * ohne zu tippen etwas findet. Gewählt wird mit Maus, Pfeiltasten und Enter.
 */
export function ProductPicker({ onPick, purpose = 'sale' }: { onPick: (p: ProductOption) => void; purpose?: 'sale' | 'stock' }) {
  const uid = useId();
  const { hasModule } = useSession();
  const [text, setText] = useState('');
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(0);
  const found = useQuery({
    queryKey: ['product-options', purpose, text.trim()],
    queryFn: async () => (await api<{ data: ProductOption[] }>('GET', `/products/options${queryString({ q: text.trim(), limit: 12, purpose: purpose === 'stock' ? 'stock' : undefined })}`)).data,
    enabled: open && hasModule('products'),
    staleTime: 30_000,
  });
  if (!hasModule('products')) return null;
  const list = found.data ?? [];
  // Ware im Lager ohne Bestand ist nicht lieferbar und für den Verkauf nicht wählbar. Im Lager selbst (purpose stock) gilt das nicht.
  const unavailable = (p: ProductOption) => purpose === 'sale' && p.stockMilli !== null && p.stockMilli <= 0;
  const pick = (p: ProductOption) => {
    if (unavailable(p)) return;
    onPick(p);
    setText('');
    setOpen(false);
  };

  return (
    <div className="picker__box offer-editor__product">
      <input
        className="input"
        role="combobox"
        aria-label="Produkt hinzufügen"
        aria-expanded={open && list.length > 0}
        aria-controls={`${uid}-list`}
        aria-activedescendant={open && list[active] ? `${uid}-${active}` : undefined}
        value={text}
        placeholder="Produkt hinzufügen: Name, Artikelnummer oder EAN …"
        onChange={(e) => {
          setText(e.target.value);
          setActive(0);
          setOpen(true);
        }}
        onFocus={() => setOpen(true)}
        onBlur={() => setTimeout(() => setOpen(false), 150)}
        onKeyDown={(e) => {
          if (e.key === 'ArrowDown') {
            e.preventDefault();
            setOpen(true);
            setActive((a) => Math.min(a + 1, Math.max(list.length - 1, 0)));
          } else if (e.key === 'ArrowUp') {
            e.preventDefault();
            setActive((a) => Math.max(a - 1, 0));
          } else if (e.key === 'Enter' && open && list[active]) {
            e.preventDefault();
            pick(list[active]);
          } else if (e.key === 'Escape') setOpen(false);
        }}
      />
      {open && found.data && (
        <ul id={`${uid}-list`} className="picker__list" role="listbox">
          {list.length === 0 && <li className="picker__empty">{text.trim() ? 'Kein aktives Produkt gefunden' : 'Noch keine Produkte angelegt'}</li>}
          {list.map((p, i) => (
            <li
              key={p.id}
              id={`${uid}-${i}`}
              role="option"
              aria-selected={i === active}
              className={`picker__option product-option${i === active ? ' is-active' : ''}${unavailable(p) ? ' is-unavailable' : ''}`}
              aria-disabled={unavailable(p) || undefined}
              onMouseEnter={() => setActive(i)}
              onMouseDown={(e) => {
                e.preventDefault();
                pick(p);
              }}
            >
              <span>
                {p.name}
                {p.sku && <span className="muted small"> · {p.sku}</span>}
              </span>
              <span className="muted small">
                {unavailable(p) ? 'nicht verfügbar' : `${formatCents(p.salePriceCents)} / ${p.unitSymbol}${purpose === 'sale' && p.stockMilli !== null ? ` · ${formatQuantityMilli(p.stockMilli)} auf Lager` : ''}`}
              </span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
